import { type InjectedFormikProps, withFormik } from "formik";
import type moment from "moment";
import { useState } from "react";

import { BTPopover } from "@btnet/core-components";

import yup from "utilities/form/yup";

import { BTCheckbox } from "commonComponents/btWrappers/BTCheckbox/BTCheckbox";
import { BTDatePicker } from "commonComponents/btWrappers/BTDatePicker/BTDatePicker";
import { BTFormItemAutomatic } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTFormattedPlural } from "commonComponents/btWrappers/BTFormattedPlural/BTFormattedPlural";
import {
    BulkAction,
    type BulkActionResponse,
    type IBulkActionStep,
} from "commonComponents/entity/bulkAction/BulkAction/BulkAction";

import "./SendToAccountingBulkAction.less";

interface ISendToAccountingBulkActionProps {
    validIds: number[];
    invalidIds: number[];
    integrationName: string | undefined;
    accountingTermForPrimaryOrSecondaryJobEntity: string | undefined;
    canMarkAsBillableToOwner: boolean | undefined;
    isBillableToOwnerCheckedPreference: boolean | undefined;
    onClose: () => Promise<void>;
    onSendToAccounting: (
        purchaseOrderPaymentIds: number[],
        markBillableToOwner: boolean,
        invoiceDate: moment.Moment | undefined,
    ) => Promise<BulkActionResponse>;
}

interface ISendToAccountingBulkActionFormValues {
    invoiceDate: moment.Moment | undefined;
    invoiceDateChecked: boolean;
}

const SendToAccountingResults: React.FunctionComponent<{
    totalCount: number;
    successfulCount: number;
    integrationName: string;
}> = ({ totalCount, successfulCount, integrationName }) => (
    <div className="SendToAccountingResults text-center">
        {successfulCount} of {totalCount} items were successfully sent.
        {successfulCount !== totalCount && (
            <div className="text-left ErrorMessages">
                One or more of the selected items could not be sent to {integrationName}. This could
                happen because of the following reasons.
                <ul>
                    <li>There are issues with an item's {integrationName} connections</li>
                    <li>Item has already been sent to {integrationName}</li>
                    <li>Item is unassigned</li>
                    <li>Item is in "Void", "Online Payment Failed", or "Draft" status</li>
                    <li>Item is assigned to an internal user</li>
                    <li>Item does not contain a valid line item amount</li>
                </ul>
            </div>
        )}
    </div>
);

export const SendToAccountingBulkActionInternal: React.FunctionComponent<
    InjectedFormikProps<ISendToAccountingBulkActionProps, ISendToAccountingBulkActionFormValues>
> = ({
    values,
    validIds,
    invalidIds,
    integrationName,
    accountingTermForPrimaryOrSecondaryJobEntity,
    canMarkAsBillableToOwner,
    isBillableToOwnerCheckedPreference,
    setFieldValue,
    onClose,
    onSendToAccounting,
    setSubmitting,
    handleSubmit,
    validateForm,
}) => {
    const [markBillableToOwner, setMarkBillableToOwner] = useState(
        isBillableToOwnerCheckedPreference ?? false,
    );

    // BT-FIN [Daric.Teske] [2020-11-13] We already know which payments are going to be valid, but the current display still includes the invalid for
    // any counts. We should revisit this later and see if we can improve this workflow at all.
    const totalPaymentCount = validIds.length + invalidIds.length;
    const handleCustomSubmit = async (step: IBulkActionStep) => {
        handleSubmit();
        setSubmitting(true);
        const formErrors = await validateForm(values);
        if (Object.keys(formErrors).length === 0) {
            await sendToAccounting(step);
        }
        setSubmitting(false);
    };

    const sendToAccounting = async (step: IBulkActionStep) => {
        step.loading({
            primaryLabel: "Sending...",
            secondaryLabel: (
                <>
                    Sending {totalPaymentCount}{" "}
                    <BTFormattedPlural value={totalPaymentCount} one="payment" other="payments" />
                </>
            ),
        });

        let successfulCount = 0;
        try {
            const resp = await onSendToAccounting(
                validIds,
                markBillableToOwner,
                values.invoiceDateChecked ? values.invoiceDate : undefined,
            );
            successfulCount = resp.successCount;
        } catch (e) {
            // swallow any errors, they'll be surfaced at the end
        }

        if (successfulCount === 0) {
            step.failure({
                primaryLabel: "Unable to Send",
                secondaryLabel: (
                    <SendToAccountingResults
                        totalCount={totalPaymentCount}
                        successfulCount={successfulCount}
                        integrationName={integrationName!}
                    />
                ),
                onOk: onClose,
            });
        } else if (successfulCount < totalPaymentCount) {
            step.warning({
                primaryLabel: "Unable to Send",
                secondaryLabel: (
                    <SendToAccountingResults
                        totalCount={totalPaymentCount}
                        successfulCount={successfulCount}
                        integrationName={integrationName!}
                    />
                ),
                onOk: onClose,
            });
        } else {
            step.complete({
                primaryLabel: "Sending Complete!",
                secondaryLabel: (
                    <SendToAccountingResults
                        totalCount={totalPaymentCount}
                        successfulCount={successfulCount}
                        integrationName={integrationName!}
                    />
                ),
                onOk: onClose,
            });
        }
    };

    return (
        <BulkAction
            onComplete={onClose}
            onCancel={onClose}
            fitToContent={true}
            renderSteps={[
                (step) =>
                    step
                        .header(
                            <>
                                Send {totalPaymentCount}{" "}
                                <BTFormattedPlural
                                    value={totalPaymentCount}
                                    one="Item"
                                    other="Items"
                                />{" "}
                                to {integrationName}
                            </>,
                        )
                        .content(
                            <>
                                <div>Are you sure you want to send to {integrationName}?</div>
                                <br />
                                {canMarkAsBillableToOwner && (
                                    <BTCheckbox
                                        id="markBillableToOwner"
                                        data-testid="markBillableToOwner"
                                        checked={markBillableToOwner}
                                        onChange={(e) => setMarkBillableToOwner(e.target.checked)}
                                    >
                                        Mark as Billable to Client
                                        <BTPopover
                                            content={`By checking this box, Buildertrend will mark the corresponding bill line items as 'billable' and associate them with the ${integrationName} ${accountingTermForPrimaryOrSecondaryJobEntity}.`}
                                        />
                                    </BTCheckbox>
                                )}
                                <>
                                    <br />
                                    <BTCheckbox<ISendToAccountingBulkActionFormValues>
                                        id="invoiceDateChecked"
                                        data-testid="invoiceDateChecked"
                                        checked={values.invoiceDateChecked}
                                        onChange={(e) =>
                                            setFieldValue("invoiceDateChecked", e.target.checked)
                                        }
                                    >
                                        Update Invoice Date
                                    </BTCheckbox>
                                    {values.invoiceDateChecked && (
                                        <BTFormItemAutomatic<ISendToAccountingBulkActionFormValues> id="invoiceDate">
                                            <BTDatePicker
                                                id="invoiceDate"
                                                data-testid="invoiceDate"
                                                value={values.invoiceDate}
                                                onChange={setFieldValue}
                                            />
                                        </BTFormItemAutomatic>
                                    )}
                                </>
                            </>,
                        )
                        .continueButton(async () => {
                            await handleCustomSubmit(step);
                        }, "Continue")
                        .render(),
            ]}
        />
    );
};

const SendToAccountingBulkValidators = yup.object().shape<ISendToAccountingBulkActionFormValues>({
    invoiceDateChecked: yup.bool().label("Update Invoice Date"),
    invoiceDate: yup
        .mixed()
        .validMomentDate("date")
        .when("invoiceDateChecked", {
            is: true,
            then: yup.mixed().validMomentDate("date").required().label("Invoice Date"),
            otherwise: yup.mixed().validMomentDate("date"),
        })
        .label("Invoice Date"),
});

export const SendToAccountingBulkAction = withFormik<
    ISendToAccountingBulkActionProps,
    ISendToAccountingBulkActionFormValues
>({
    mapPropsToValues: () => ({
        invoiceDateChecked: false,
        invoiceDate: undefined,
    }),
    validationSchema: () => SendToAccountingBulkValidators,
    validateOnChange: true,
    handleSubmit: () => {},
})(SendToAccountingBulkActionInternal);
